/**
 * m3 echo Frontend - Create Custom Autopilot Dialog
 *
 * Dialog for creating new custom autopilots.
 * Allows selection of prompts, models, frequency, and duration.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  Paper,
  IconButton,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  customAutopilotApi,
  tenantApi,
  TenantOption,
  TenantPromptsResponse,
  CustomAutopilotCreate,
} from '../../services/admin';
import { settingsApi } from '../../services/api';

// Configuration options (must match backend validators)
const FREQUENCY_OPTIONS = [
  { value: 30, label: '30 Minuten' },
  { value: 60, label: '1 Stunde' },
  { value: 120, label: '2 Stunden' },
  { value: 240, label: '4 Stunden' },
];

const DURATION_OPTIONS = [
  { value: 24, label: '1 Tag' },
  { value: 48, label: '2 Tage' },
  { value: 72, label: '3 Tage' },
  { value: 168, label: '7 Tage' },
];

interface PromptConfig {
  prompt_id: number;
  prompt_content: string;
  placeholders: string[];
  prompt_values: Record<string, string>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateCustomAutopilotDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form state
  const [name, setName] = useState('');
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrompts, setSelectedPrompts] = useState<PromptConfig[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [frequencyMinutes, setFrequencyMinutes] = useState(60);
  const [durationHours, setDurationHours] = useState(24);

  // Data state
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [availablePrompts, setAvailablePrompts] = useState<TenantPromptsResponse['prompts']>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load tenants and models on mount
  useEffect(() => {
    if (open) {
      loadTenants();
      loadModels();
    }
  }, [open]);

  // Load prompts when tenant changes
  useEffect(() => {
    if (tenantId) {
      loadPrompts(tenantId);
    } else {
      setAvailablePrompts([]);
      setSelectedPrompts([]);
    }
  }, [tenantId]);

  const loadTenants = async () => {
    setLoadingTenants(true);
    try {
      const response = await tenantApi.getOptions();
      // Filter to only maco test tenant
      const macoTenant = response.data.find(t => t.name === 'maco test');
      if (macoTenant) {
        setTenants([macoTenant]);
        setTenantId(macoTenant.id);
      } else {
        setTenants([]);
        setError('maco test Tenant nicht gefunden');
      }
    } catch (err: any) {
      console.error('Error loading tenants:', err);
      setError('Fehler beim Laden der Tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadPrompts = async (tid: number) => {
    setLoadingPrompts(true);
    try {
      const response = await customAutopilotApi.getTenantPrompts(tid);
      setAvailablePrompts(response.data.prompts);
    } catch (err: any) {
      console.error('Error loading prompts:', err);
      setError('Fehler beim Laden der Prompts');
    } finally {
      setLoadingPrompts(false);
    }
  };

  const loadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await settingsApi.getAvailableModels();
      // Filter to only active models and extract model names
      const activeModels = response.data
        .filter((m: any) => m.is_active)
        .map((m: any) => m.model_name);
      setAvailableModels(activeModels);
    } catch (err: any) {
      console.error('Error loading models:', err);
      setError('Fehler beim Laden der Modelle');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleAddPrompt = (promptId: number) => {
    const prompt = availablePrompts.find(p => p.id === promptId);
    if (!prompt) return;

    // Check if already added
    if (selectedPrompts.some(p => p.prompt_id === promptId)) {
      return;
    }

    // Initialize placeholder values
    const promptValues: Record<string, string> = {};
    prompt.placeholders.forEach(ph => {
      promptValues[ph] = '';
    });

    setSelectedPrompts([
      ...selectedPrompts,
      {
        prompt_id: promptId,
        prompt_content: prompt.content,
        placeholders: prompt.placeholders,
        prompt_values: promptValues,
      },
    ]);
  };

  const handleRemovePrompt = (index: number) => {
    setSelectedPrompts(selectedPrompts.filter((_, i) => i !== index));
  };

  const handlePlaceholderChange = (promptIndex: number, placeholder: string, value: string) => {
    const updated = [...selectedPrompts];
    updated[promptIndex] = {
      ...updated[promptIndex],
      prompt_values: {
        ...updated[promptIndex].prompt_values,
        [placeholder]: value,
      },
    };
    setSelectedPrompts(updated);
  };

  const handleModelToggle = (model: string) => {
    if (selectedModels.includes(model)) {
      setSelectedModels(selectedModels.filter(m => m !== model));
    } else {
      setSelectedModels([...selectedModels, model]);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!tenantId) {
      setError('Bitte wählen Sie einen Tenant aus');
      return;
    }

    if (selectedPrompts.length === 0) {
      setError('Bitte wählen Sie mindestens einen Prompt aus');
      return;
    }

    if (selectedModels.length === 0) {
      setError('Bitte wählen Sie mindestens ein Modell aus');
      return;
    }

    // Check all placeholders are filled
    for (const prompt of selectedPrompts) {
      for (const ph of prompt.placeholders) {
        if (!prompt.prompt_values[ph]?.trim()) {
          setError(`Bitte füllen Sie alle Platzhalter aus (${ph})`);
          return;
        }
      }
    }

    setLoading(true);
    setError(null);

    try {
      const data: CustomAutopilotCreate = {
        name: name.trim() || undefined,
        tenant_id: tenantId,
        search_term: searchTerm.trim() || undefined,
        prompts: selectedPrompts.map(p => ({
          prompt_id: p.prompt_id,
          prompt_values: p.prompt_values,
        })),
        models: selectedModels,
        frequency_minutes: frequencyMinutes,
        duration_hours: durationHours,
      };

      await customAutopilotApi.create(data);
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error('Error creating autopilot:', err);
      setError(err.response?.data?.detail || 'Fehler beim Erstellen des Custom Autopilots');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setTenantId(null);
    setSearchTerm('');
    setSelectedPrompts([]);
    setSelectedModels([]);
    setFrequencyMinutes(60);
    setDurationHours(24);
    setError(null);
    onClose();
  };

  // Calculate expected runs
  const expectedRuns = Math.floor((durationHours * 60) / frequencyMinutes);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Neuer Custom Autopilot</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* Name (optional) */}
          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder="z.B. Timing-Test Q1 2025"
          />

          {/* Tenant Selection (auto-selected to maco test) */}
          <FormControl fullWidth disabled>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={tenantId || ''}
              label="Tenant"
            >
              {loadingTenants ? (
                <MenuItem disabled>Wird geladen...</MenuItem>
              ) : (
                tenants.map(tenant => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.display_name} ({tenant.name})
                  </MenuItem>
                ))
              )}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Custom Autopilots sind nur für maco test verfügbar
            </Typography>
          </FormControl>

          {/* Search Term for Layer 2 */}
          <TextField
            label="Suchbegriff (Layer 2)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            placeholder="z.B. Firmenname für Layer 2 Analyse"
            helperText="Der Suchbegriff wird verwendet, um in den KI-Antworten nach Erwähnungen zu suchen und einen Score zu berechnen."
          />

          {/* Prompt Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Prompts auswählen
            </Typography>

            {loadingPrompts ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Prompt hinzufügen</InputLabel>
                  <Select
                    value=""
                    label="Prompt hinzufügen"
                    onChange={(e) => handleAddPrompt(Number(e.target.value))}
                  >
                    {availablePrompts.map(prompt => (
                      <MenuItem
                        key={prompt.id}
                        value={prompt.id}
                        disabled={selectedPrompts.some(p => p.prompt_id === prompt.id)}
                      >
                        #{prompt.id}: {prompt.content.substring(0, 80)}...
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Selected Prompts */}
                {selectedPrompts.map((prompt, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }} variant="outlined">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2">
                        Prompt #{prompt.prompt_id}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemovePrompt(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {prompt.prompt_content.substring(0, 150)}...
                    </Typography>

                    {/* Placeholder inputs */}
                    {prompt.placeholders.length > 0 && (
                      <Grid container spacing={2}>
                        {prompt.placeholders.map(ph => (
                          <Grid item xs={12} sm={6} key={ph}>
                            <TextField
                              label={ph}
                              value={prompt.prompt_values[ph] || ''}
                              onChange={(e) => handlePlaceholderChange(index, ph, e.target.value)}
                              fullWidth
                              size="small"
                              required
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Paper>
                ))}

                {selectedPrompts.length === 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Wählen Sie mindestens einen Prompt aus dem Dropdown aus.
                  </Alert>
                )}
              </>
            )}
          </Box>

          {/* Model Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Modelle auswählen
            </Typography>
            {loadingModels ? (
              <CircularProgress size={24} />
            ) : availableModels.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableModels.map(model => (
                  <FormControlLabel
                    key={model}
                    control={
                      <Checkbox
                        checked={selectedModels.includes(model)}
                        onChange={() => handleModelToggle(model)}
                        size="small"
                      />
                    }
                    label={<Chip label={model} size="small" variant={selectedModels.includes(model) ? 'filled' : 'outlined'} />}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="warning">
                Keine aktiven Modelle verfügbar. Bitte aktivieren Sie Modelle in den Einstellungen.
              </Alert>
            )}
            {selectedModels.length === 0 && availableModels.length > 0 && (
              <Typography variant="caption" color="error">
                Mindestens ein Modell erforderlich
              </Typography>
            )}
          </Box>

          {/* Frequency and Duration */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Frequenz</InputLabel>
                <Select
                  value={frequencyMinutes}
                  label="Frequenz"
                  onChange={(e) => setFrequencyMinutes(Number(e.target.value))}
                >
                  {FREQUENCY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Dauer</InputLabel>
                <Select
                  value={durationHours}
                  label="Dauer"
                  onChange={(e) => setDurationHours(Number(e.target.value))}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Summary */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              Zusammenfassung
            </Typography>
            <Typography variant="body2">
              <strong>Erwartete Runs:</strong> {expectedRuns}
            </Typography>
            <Typography variant="body2">
              <strong>Start:</strong> Nächste Mitternacht (Europe/Berlin)
            </Typography>
            <Typography variant="body2">
              <strong>Ende:</strong> Nach {durationHours} Stunden
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Runs die in das 7-Uhr-Zeitfenster (6:45-7:15) fallen, werden auf 7:30 verschoben,
              um Konflikte mit regulären Autopilots zu vermeiden.
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Abbrechen</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !tenantId || selectedPrompts.length === 0 || selectedModels.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCustomAutopilotDialog;
